import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Library, Share } from 'lucide-react';
import { z } from 'zod';

import {
  getGetAllLibrariesQueryKey,
  getGetLibraryDataChecksQueryKey,
  useGetLibrary,
  useGetLibraryDataChecks,
} from '@/api/gen/data-harmonization-v2-libraries';
import { DHActionsDropdown } from '@/components/features/data-harmonization/DHActionsDropdown';
import { DeleteLibraryConfirmationModal } from '@/components/features/data-harmonization/landingPage/modal/DeleteLibraryModal';
import { PageHeaderContent } from '@/components/features/page-layout/PageHeaderContent';
import { useDHPermissions } from '@/features/data-harmonization/hooks/useDHPermissions';
import { EditLibraryModal } from '@/features/data-harmonization/landingPage/modal/EditLibraryModal';
import { DataChecksTable } from '@/features/data-harmonization/library/DataChecksTable';
import { ExportToDashboardModal } from '@/features/data-harmonization/library/modal/ExportToDasboardModal';
import { DHPageAccessProvider } from '@/features/data-harmonization/providers/DHPageAccessProvider';
import { useModal } from '@/hooks/use-modal';
import { Button } from '@/ui/Button';

export const Route = createFileRoute('/_protected/t/$tenantId/data-harmonization/library/$libraryId')({
  component: DataHarmonizationLibraryRoute,
  validateSearch: z.object({
    search: z.coerce.string().optional(),
  }),
});

const LIBRARY_PAGE_PERMISSION = 'page:libraries';

function DataHarmonizationLibraryRoute() {
  const router = useRouter();
  const { libraryId } = Route.useParams();
  const { search } = Route.useSearch();
  const { can } = useDHPermissions();

  const queryClient = useQueryClient();

  const hasAccess = can(LIBRARY_PAGE_PERMISSION, 'any');

  const { data: libraryData, isLoading: isLoadingLibrary } = useGetLibrary(
    { libraryId },
    { query: { enabled: hasAccess } },
  );
  const { data: dataChecks, isLoading: isLoadingDataChecks } = useGetLibraryDataChecks(
    { libraryId },
    { query: { enabled: hasAccess } },
  );

  const [selectedDataCheckIds, setSelectedDataCheckIds] = useState<Set<string>>(() => new Set());

  const {
    open: isExportToDashboardModalOpen,
    handleOpen: handleExportToDashboardModalOpen,
    handleClose: handleExportToDashboardModalClose,
  } = useModal();

  const {
    open: isEditLibraryModalOpen,
    handleOpen: handleEditLibraryModalOpen,
    handleClose: handleEditLibraryModalClose,
  } = useModal();

  const {
    open: isDeleteLibraryModalOpen,
    handleOpen: handleDeleteLibraryModalOpen,
    handleClose: handleDeleteLibraryModalClose,
  } = useModal();

  const handleGoBack = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetAllLibrariesQueryKey() });
    router.history.back();
  }, [queryClient, router]);

  const handleCreateNewDashboard = useCallback(() => {
    handleExportToDashboardModalOpen();
    queryClient.invalidateQueries({
      queryKey: getGetLibraryDataChecksQueryKey({ libraryId }),
    });
  }, [handleExportToDashboardModalOpen, queryClient, libraryId]);

  return (
    <DHPageAccessProvider permission={LIBRARY_PAGE_PERMISSION} isLoading={isLoadingLibrary} dashboardId="any">
      {!libraryData ? (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <h1 className="text-2xl font-bold text-white">Library Not Found</h1>
          <p className="text-gray-400">The library with ID &quot;{libraryId}&quot; does not exist.</p>
          <Button className="hover:bg-white/10" onClick={handleGoBack}>
            Go Back
          </Button>
        </div>
      ) : (
        <>
          <PageHeaderContent>
            <Button
              size="sm"
              variant="outline"
              className="flex shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 p-2 hover:bg-white/10"
              Icon={Share}
              tooltipContent="Export all Data Checks to a new Dashboard"
              onClick={handleCreateNewDashboard}
              disabled={dataChecks?.data_checks?.length === 0}
            >
              Create New Dashboard
            </Button>
            {can('action:manage-library', 'any') && (
              <DHActionsDropdown
                onEdit={handleEditLibraryModalOpen}
                onDelete={handleDeleteLibraryModalOpen}
                isDeleteDisabled={(libraryData.data_check_count ?? 0) > 0}
                deleteTooltipContent="Library contains 1 or more data checks"
                variant="outlined-button"
              />
            )}
          </PageHeaderContent>
          <div className="flex flex-col items-start justify-between">
            <div className="mb-6 w-full flex items-start justify-between">
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  <Library className="size-4 shrink-0 text-white/40" />
                  <span className="text-sm text-white/40">Library</span>
                </div>
                <h1 className="text-xl font-bold text-[#D7DADC]">{libraryData.name}</h1>
                <p className="max-w-4xl text-sm font-normal text-white/70">{libraryData.description}</p>
              </div>
            </div>
          </div>

          <DataChecksTable
            data={dataChecks?.data_checks ?? []}
            libraryId={libraryId}
            isLoading={isLoadingDataChecks}
            selectedDataCheckIds={selectedDataCheckIds}
            setSelectedDataCheckIds={setSelectedDataCheckIds}
            initialSearchQuery={search}
          />

          {isExportToDashboardModalOpen && (
            <ExportToDashboardModal
              open={isExportToDashboardModalOpen}
              onClose={handleExportToDashboardModalClose}
              selectedDataCheckIds={
                selectedDataCheckIds.size > 0
                  ? Array.from(selectedDataCheckIds)
                  : (dataChecks?.data_checks?.map((d) => d.data_check_id) ?? [])
              }
            />
          )}
          {isEditLibraryModalOpen && (
            <EditLibraryModal
              open={isEditLibraryModalOpen}
              library={libraryData}
              onClose={handleEditLibraryModalClose}
            />
          )}
          {isDeleteLibraryModalOpen && (
            <DeleteLibraryConfirmationModal
              open={isDeleteLibraryModalOpen}
              library={libraryData}
              onClose={handleDeleteLibraryModalClose}
            />
          )}
        </>
      )}
    </DHPageAccessProvider>
  );
}
